import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, LayoutGrid, Landmark, Plus, Lock } from "lucide-react";
import { Reveal } from "./Reveal";
import { IuniFlower } from "./IuniFlower";
import { FullWaitlistForm } from "./WaitlistForms";

// ---------- Pain points ----------
const PAINS = [
  {
    icon: MessageCircle,
    title: "Il caos delle chat",
    body: "Basta rincorrere gli ospiti per sapere se vengono o se hanno intolleranze.",
  },
  {
    icon: LayoutGrid,
    title: "L'incubo dei tavoli",
    body: "Dimentica i fogli di carta scarabocchiati per incastrare parenti e amici.",
  },
  {
    icon: Landmark,
    title: "L'imbarazzo dell'IBAN",
    body: "I contributi arrivano prima della cerimonia, permettendoti di saldare gli acconti ai fornitori in totale serenità, direttamente dalla piattaforma.",
  },
];

export function PainPoints() {
  return (
    <section className="bg-[#FBFBFB] py-24 sm:py-32" data-testid="pain-points-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <Reveal>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-center text-[#1A1A1A] max-w-3xl mx-auto">
            Organizzare non dovrebbe essere un secondo lavoro.
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-3 gap-6 mt-16">
          {PAINS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.12}>
              <div
                data-testid={`pain-card-${i + 1}`}
                className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100/60 p-8 h-full hover:-translate-y-1 hover:shadow-[0_16px_40px_rgb(0,0,0,0.07)] transition-[transform,box-shadow]"
              >
                <span className="w-12 h-12 rounded-2xl bg-[rgba(224,122,95,0.1)] flex items-center justify-center">
                  <p.icon size={20} className="text-[#E07A5F]" strokeWidth={1.8} />
                </span>
                <h3 className="font-display text-xl sm:text-2xl text-[#1A1A1A] mt-6">{p.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mt-3">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- Trust banner ----------
export function TrustBanner() {
  return (
    <section className="relative overflow-hidden py-28 sm:py-36" data-testid="trust-banner">
      {/* background photo */}
      <img
        src="/hero-couple.webp"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      {/* readability scrims: dark gradient + warm sunset tint */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/60" />
      <div className="absolute inset-0 bg-[#C2492F]/20 mix-blend-multiply" />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 55% at 50% 55%, rgba(0,0,0,0.35), transparent 70%)",
        }}
      />
      <Reveal className="relative max-w-3xl mx-auto px-6 text-center">
        <div className="flex justify-center mb-8 drop-shadow-[0_2px_12px_rgba(0,0,0,0.4)]">
          <IuniFlower size={44} />
        </div>
        <p
          className="font-display text-2xl sm:text-3xl lg:text-4xl text-white leading-snug [text-shadow:0_2px_24px_rgba(0,0,0,0.45)]"
          data-testid="trust-banner-text"
        >
          Crediamo che un matrimonio non debba essere rimandato per una questione di budget.
          Trasformiamo i regali di chi vi vuole bene in un supporto semplice, trasparente e
          concreto fin dai preparativi.
        </p>
      </Reveal>
    </section>
  );
}

// ---------- Bottom CTA ----------
export function BottomCTA({ onSuccess }) {
  return (
    <section id="waitlist-section" className="bg-[#FBFBFB] py-24 sm:py-32" data-testid="bottom-cta-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <Reveal>
          <div className="max-w-2xl mx-auto bg-white rounded-[2rem] shadow-[0_30px_80px_-24px_rgba(0,0,0,0.12)] border border-gray-100/60 p-8 sm:p-12 text-center">
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-[#1A1A1A]">
              Sei pronto a riprenderti il tuo tempo?
            </h2>
            <p className="text-base text-gray-500 mt-4">
              Accedi in anteprima alla versione beta e unisciti alle prime 50 coppie. Organizza il
              tuo matrimonio a zero stress.
            </p>
            <FullWaitlistForm onSuccess={onSuccess} />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ---------- FAQ ----------
const FAQS = [
  {
    q: "Cosa significa che iuni è in fase di “Beta privata”?",
    a: "Significa che stiamo aprendo le porte della piattaforma a un numero ristretto di coppie selezionate (solo 50 per questa fase) prima del lancio ufficiale. Iscrivendoti alla lista d'attesa avrai la possibilità di utilizzare il software in anteprima, ricevere supporto dedicato dal nostro team per configurare il tuo evento e bloccare condizioni agevolate esclusive riservate ai primi utilizzatori.",
  },
  {
    q: "Quanto costa utilizzare iuni? Ci sono costi nascosti?",
    a: "Iniziare a progettare il tuo matrimonio su iuni è gratuito: puoi creare l'evento, testare l'interfaccia e iniziare a impostare la lista invitati senza inserire alcuna carta di credito. Per la gestione finanziaria (la Busta Digitale), applichiamo una commissione trasparente del 3% sulle transazioni ricevute, che copre i costi dei circuiti bancari internazionali e attiva la protezione sui tuoi pagamenti ai fornitori. Le funzioni avanzate (come l'AI Concierge 24/7 e l'export esecutivo per il catering) sono attivabili con un pacchetto una tantum senza abbonamenti ricorrenti.",
  },
  {
    q: "I soldi dei nostri regali sono al sicuro? Come li trasferiamo sul nostro conto?",
    a: "La sicurezza è la nostra priorità assoluta. iuni non trattiene i tuoi fondi su conti proprietari amatoriali, ma si appoggia a infrastrutture bancarie certificate a livello europeo (conformi alla direttiva PSD2 e crittografate). Ogni regalo ricevuto finisce in un salvadanaio digitale blindato intestato a voi: potrete trasferire la liquidità sul vostro conto corrente bancario in qualsiasi momento con un click, oppure usarla direttamente in piattaforma per saldare gli acconti dei fornitori in totale sicurezza (sistema Escrow).",
  },
  {
    q: "I miei invitati devono scaricare un'app o registrarsi per confermare la presenza o farci un regalo?",
    a: "Assolutamente no. Sappiamo che tra gli invitati ci sono persone di tutte le età. Per gli ospiti l'esperienza è immediata: ricevono il vostro link personalizzato (via WhatsApp o email) e si apre una pagina web elegante e leggerissima. Possono confermare la presenza, segnalare intolleranze alimentari in due tocchi e inviare un regalo in pochi secondi usando Apple Pay, Google Pay, carta di credito o bonifico, senza dover creare alcun account o ricordare password.",
  },
  {
    q: "Abbiamo già iniziato a fare la lista invitati su Excel. Dobbiamo reinserire tutto da capo?",
    a: "No, non perderete nemmeno un minuto del lavoro già fatto. iuni permette di importare con un click il vostro file Excel o CSV esistente: il sistema leggerà automaticamente nomi, nuclei familiari ed eventuali note, trasformando istantaneamente una tabella caotica nella vostra nuova dashboard interattiva pronta per creare i tavoli.",
  },
  {
    q: "Come funziona esattamente l'AI Concierge per gli ospiti?",
    a: "È il vostro assistente personale che vi evita di fare da centralino nei giorni prima delle nozze. Voi inserite nella dashboard le informazioni chiave dell'evento (orari, indirizzi, parcheggi consigliati, dress code, hotel convenzionati). Quando un invitato ha un dubbio, invece di scrivere a voi su WhatsApp, chiede alla chat dell'evento: l'Intelligenza Artificiale risponde istantaneamente in modo cortese e preciso 24 ore su 24.",
  },
  {
    q: "Il nostro catering o la nostra wedding planner non usano ancora iuni. Possiamo usarlo lo stesso?",
    a: "Certamente. Siete voi i proprietari del vostro evento. Potete usare iuni in totale autonomia e, quando i tavoli e le intolleranze sono definitivi, generare con un click un documento esecutivo perfetto (PDF o link di sola lettura) da inviare al catering o alla location. Se invece i vostri fornitori desiderano collaborare attivamente, potrete invitarli gratuitamente nella vostra dashboard con un accesso dedicato.",
  },
];

export function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section className="bg-white py-24 sm:py-32" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6">
        <Reveal className="text-center">
          <span className="inline-block text-[11px] font-bold uppercase tracking-[0.22em] text-[#E07A5F]">
            Chiarezza e trasparenza
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A] mt-4">
            Domande frequenti
          </h2>
          <p className="text-base text-gray-500 mt-4">
            Tutto quello che devi sapere sul funzionamento di iuni e sul programma Beta.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-14">
          <div>
            {FAQS.map((f, i) => (
              <div key={i} className="border-b border-gray-200/80">
                <button
                  data-testid={`faq-question-${i + 1}`}
                  onClick={() => setOpen(open === i ? null : i)}
                  aria-expanded={open === i}
                  className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                >
                  <span className="text-lg font-medium text-gray-900 group-hover:text-[#C2492F] transition-colors">
                    {f.q}
                  </span>
                  <Plus
                    size={20}
                    strokeWidth={1.5}
                    className={`shrink-0 transition-transform duration-300 ${
                      open === i ? "rotate-45 text-[#E07A5F]" : "rotate-0 text-gray-400"
                    }`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p
                        data-testid={`faq-answer-${i + 1}`}
                        className="text-sm sm:text-base text-gray-500 leading-relaxed pb-7 pr-8"
                      >
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </Reveal>

        <p className="text-center text-sm text-gray-400 mt-10">
          Hai altre domande?{" "}
          <button
            data-testid="faq-back-to-form-link"
            onClick={() => document.getElementById("waitlist-section")?.scrollIntoView({ behavior: "smooth" })}
            className="text-[#C2492F] underline underline-offset-4 decoration-[#E07A5F]/40 hover:decoration-[#E07A5F] transition-colors"
          >
            Scrivici o richiedi l'invito qui sopra
          </button>
        </p>
      </div>
    </section>
  );
}

// ---------- Footer ----------
import { Link } from "react-router-dom";

export function Footer() {
  return (
    <footer className="border-t border-gray-100 py-10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-sm text-gray-400">© 2026 iuni. Tutti i diritti riservati.</p>
        <div className="flex items-center gap-5">
          <Link
            to="/privacy-policy"
            data-testid="footer-privacy-link"
            className="text-sm text-gray-400 hover:text-[#C2492F] transition-colors"
          >
            Privacy Policy
          </Link>
          <Link
            to="/admin"
            data-testid="footer-admin-link"
            className="inline-flex items-center gap-1.5 text-xs text-gray-300 hover:text-[#C2492F] transition-colors"
          >
            <Lock size={12} />
            Admin
          </Link>
        </div>
      </div>
    </footer>
  );
}
